import { ReactNode } from "react";
import { Stack } from "@altimateai/lego";
import { Button, Checkbox, Label, Typography } from "@altimateai/lego";

export interface ExportParams {
  upstream: boolean;
  downstream: boolean;
}

export interface ColumnExportParams extends ExportParams {
  direct: boolean;
  indirect: boolean;
}

function ExportLineageModalBase<T extends ExportParams>({
  title,
  params,
  setParams,
  onExport,
  isExporting,
  error,
  validationErrors,
  children,
}: {
  title: string;
  params: T;
  setParams: React.Dispatch<React.SetStateAction<T>>;
  onExport: () => void;
  isExporting: boolean;
  error?: Error;
  validationErrors?: { condition: boolean; message: string }[];
  children?: ReactNode;
}) {
  const updateParam = (id: keyof T & string, value: boolean) =>
    setParams(prev => ({ ...prev, [id]: value }));

  return (
    <Stack direction="column" className="al-overflow-hidden">
      <Typography weight="medium" className="al-mb-0 al-text-primary al-break-all">
        {title}
      </Typography>
      {children}
      <Stack direction="column" gap="xxs">
        <Typography weight="medium" size="sm" className="al-mb-0">
          Lineage Direction
        </Typography>
        <Stack>
          <Checkbox
            id="upstream"
            checked={params.upstream}
            onCheckedChange={v => updateParam("upstream", v as boolean)}
          />
          <Label htmlFor="upstream">Upstream</Label>
        </Stack>
        <Stack>
          <Checkbox
            id="downstream"
            checked={params.downstream}
            onCheckedChange={v => updateParam("downstream", v as boolean)}
          />
          <Label htmlFor="downstream">Downstream</Label>
        </Stack>
      </Stack>
      <Button onClick={onExport}>Export</Button>
      {!params.upstream && !params.downstream && (
        <Stack className="al-bg-bg-red al-p-2 al-rounded-md">
          <Typography variant="caption" size="sm" className="al-text-xs">
            Select at least one of the upstream or downstream
          </Typography>
        </Stack>
      )}
      {validationErrors?.map(
        ({ condition, message }) =>
          condition && (
            <Stack key={message} className="al-bg-bg-red al-p-2 al-rounded-md">
              <Typography variant="caption" size="sm" className="al-text-xs">
                {message}
              </Typography>
            </Stack>
          )
      )}
      {error && (
        <Stack className="al-bg-bg-red al-p-2 al-rounded-md">
          <Typography variant="caption" size="sm" className="al-text-xs">
            Export failed. Please try again.
          </Typography>
        </Stack>
      )}
      {isExporting && (
        <Stack className="al-bg-gray-15 al-p-2 al-rounded-md">
          <div
            className="al-animate-spin al-h-4 al-w-4 al-border-2 al-border-primary al-border-t-transparent al-rounded-full"
            role="progressbar"
            aria-label="Loading"
            aria-hidden="false"
          />
          <Typography variant="caption" size="sm" className="al-text-xs">
            Export in progress. This may take up to a minute.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

export default ExportLineageModalBase;
